import { useMemo, useRef } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import topics from "@/data/topics.json";
import { Eyebrow, MaskLines } from "@/components/site/Reveal";
import { TopicBody } from "@/components/site/TopicBody";
import { Connect } from "@/components/site/Connect";
import { Seo, siteUrl } from "@/components/site/Seo";
import { topicSeo, cleanTitle } from "@/lib/seo";

const TopicSeo = ({ t }) => {
  const seo = topicSeo(t);
  const jsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        name: seo.title,
        headline: cleanTitle(t.title),
        description: seo.description,
        url: siteUrl(seo.path),
        image: siteUrl(seo.image),
        inLanguage: "en",
        isPartOf: { "@id": siteUrl("/#website") },
        publisher: { "@id": siteUrl("/#org") },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: siteUrl("/") },
          { "@type": "ListItem", position: 2, name: "Topics", item: siteUrl("/topics") },
          { "@type": "ListItem", position: 3, name: cleanTitle(t.title), item: siteUrl(seo.path) },
        ],
      },
    ],
  }), [t, seo.title, seo.description, seo.path, seo.image]);
  return <Seo {...seo} type="article" jsonLd={jsonLd} />;
};

const PageHero = ({ t, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  return (
    <section ref={ref} className="relative min-h-[70svh] flex items-end overflow-hidden">
      {t.cover && (
        <motion.div style={{ y }} className="absolute inset-0">
          <img src={t.cover} alt="" className="w-full h-full object-cover brightness-[0.4] scale-110" />
        </motion.div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-ink/20" />
      <div className="absolute inset-0 grid-lines opacity-30" />
      <div className="relative w-full px-5 sm:px-10 lg:px-16 pb-14 pt-32">
        <Link data-testid="back-to-topics" to="/topics" className="inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.25em] text-zinc-300 hover:text-lime transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> All topics
        </Link>
        <Eyebrow className="mb-4">// Topic {String(index + 1).padStart(2, "0")} / {topics.length}</Eyebrow>
        <h1 data-testid="topic-title" className="font-display font-bold uppercase leading-[0.9] text-5xl sm:text-7xl lg:text-8xl max-w-5xl break-words">
          <MaskLines onLoad key={t.slug} lines={[t.title]} />
        </h1>
      </div>
    </section>
  );
};

const Pager = ({ index }) => {
  const prev = topics[(index - 1 + topics.length) % topics.length];
  const next = topics[(index + 1) % topics.length];
  return (
    <nav className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-24">
      {[["prev", prev, ArrowLeft, "Previous"], ["next", next, ArrowRight, "Next"]].map(([k, t, Icon, label]) => (
        <Link key={k} data-testid={`topic-${k}`} to={`/${t.slug}`} className={`group rounded-3xl border border-white/10 bg-panel hover:border-lime/50 p-6 sm:p-8 transition-colors duration-300 ${k === "next" ? "sm:text-right" : ""}`}>
          <span className={`flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.25em] text-zinc-400 ${k === "next" ? "sm:justify-end" : ""}`}>
            {k === "prev" && <Icon className="w-4 h-4 text-lime transition-transform group-hover:-translate-x-1" />}
            {label}
            {k === "next" && <Icon className="w-4 h-4 text-lime transition-transform group-hover:translate-x-1" />}
          </span>
          <span className="block mt-3 font-display font-bold text-2xl sm:text-3xl group-hover:text-lime transition-colors">{t.title}</span>
        </Link>
      ))}
    </nav>
  );
};

export default function TopicPage() {
  const { slug } = useParams();
  const index = topics.findIndex((t) => t.slug === slug);
  if (index === -1) return <Navigate to="/topics" replace />;
  const t = topics[index];
  return (
    <main data-testid="topic-page">
      <TopicSeo t={t} />
      <PageHero t={t} index={index} />
      <section className="px-5 sm:px-10 lg:px-16 py-16 sm:py-20">
        <div className="max-w-3xl mx-auto">
          <TopicBody key={t.slug} blocks={t.blocks} />
          <Pager index={index} />
        </div>
      </section>
      <Connect />
    </main>
  );
}
